// ganttHelpers — pure helpers shared by CoordinationGantt: layout
// constants, date math, tree flattening, meeting recurrence expansion
// and the contractor-lane grouping used by the "By contractor" toggle.
// No React, no antd — plain functions so they stay trivially testable.

export const DAY_MS = 24 * 60 * 60 * 1000;
export const PX_PER_DAY = 26;
export const ROW_H = 30;
export const HEADER_H = 28;
export const LABEL_W = 180;

// WEEKENDS ARE SHADED ONLY WHEN A DAY IS WIDE ENOUGH TO BE WORTH SEEING.
//
// Owner, 2026-09-30: "only add the weekend barring when you zoom in". At the
// bottom of the zoom range a day is 4 px, so a weekend is an 8 px stripe
// every 26 px - that is not information, it is a texture, and it makes the
// bars harder to read rather than easier.
//
// THE NUMBER IS CHOSEN AGAINST THE PERSISTED ZOOM, NOT AGAINST TASTE. The
// zoom is a saved per-person preference (usePreference "gantt.zoom", clamped
// 4..120, default PX_PER_DAY = 26). Anyone sitting below the threshold never
// sees the feature and reports it missing, so the threshold sits WELL BELOW
// THE DEFAULT rather than in the middle of the range: at 26 - where everyone
// starts - weekends are shaded, and you have to zoom deliberately out to
// lose them.
export const WEEKEND_BAND_MIN_PX_PER_DAY = 12;

/** Saturdays and Sundays between two dates, as day-start Dates. */
export function weekendDays(from, to) {
  const out = [];
  if (!from || !to) return out;
  for (let t = new Date(from).setHours(0, 0, 0, 0); t <= new Date(to).getTime(); t += DAY_MS) {
    const d = new Date(t);
    const g = d.getDay();
    if (g === 0 || g === 6) out.push(d);
  }
  return out;
}


export const HEALTH_FILL = {
  behind: "#d4380d",
  at_risk: "#d48806",
  on_track: "#389e0d",
};
export const DONE_FILL = "#bfbfbf";

// BIM-to-Field structure: which chain a row belongs to, and the gate
// the feed computes on a field row (BIM sign-off before install).
export const PHASE_LABEL = { bim: "BIM", field: "Field", management: "Management" };
// THE STEP A ROW BELONGS TO. Ordered along the chain, BIM first.
//
// shop_drawings AND prefab WERE MISSING, and shop_drawings is the
// most-used step in the entire database - 72 rows against install's 69
// and model's 38. This list was written from a plan rather than from the
// data, and it went unnoticed because every shop-drawing row that exists
// was created by scripts/restructureBimField, never by a person picking
// the value. The owner hit it the moment he deleted 615 activities and
// started rebuilding by hand: the shop-drawings picker told him to add
// shop-drawing steps to the plan, and this dropdown would not offer one.
// Found and measured by HAL (root-47) across every project, not just his.
//
// drawings_iff, install_mains and install_branches are used ZERO times
// anywhere and are DELIBERATELY KEPT: no rows today is not proof nothing
// depends on them, and removing them is a separate question with its own
// check.
export const SCHEDULE_STEPS = [
  "model", "model_60", "model_80", "coordinate",
  "shop_drawings", "drawings_iff", "prefab", "signoff",
  "install", "install_mains", "install_branches", "field_signoff",
];

// WHAT A PERSON SEES. The STORED VALUE IS UNCHANGED - the shop-drawings
// picker, utils/shopDrawingOptions and the routes all match on the raw
// string, so this is presentation only. A dropdown offering
// "field_signoff" was asking him to read the database.
export const STEP_LABEL = {
  model: "Model",
  model_60: "Model 60%",
  model_80: "Model 80%",
  coordinate: "Coordinate",
  shop_drawings: "Shop drawings",
  // THE NEAR-MISS, LABELLED AS ONE. The owner picked drawings_iff for
  // twelve rows on 2026-10-02 because "IFF" reads as Issued For
  // Fabrication and it was the only thing in this list that looked like a
  // shop drawing - shop_drawings was missing. He has since ruled they are
  // the same stage and that he will use shop_drawings.
  //
  // The VALUE stays (zero-rows-today is not proof nothing depends on it,
  // and twelve of his rows still carry it), and it already sorts BELOW
  // shop_drawings - but sorting is not enough when two entries read the
  // same. The label now says which one he wants. Label only: the stored
  // string is untouched.
  drawings_iff: "Drawings IFF (use Shop drawings)",
  prefab: "Prefab",
  signoff: "Sign-off",
  install: "Install",
  install_mains: "Install mains",
  install_branches: "Install branches",
  field_signoff: "Field sign-off",
};
// An unknown step still has to read as something: a project restructured
// before a value existed can hold one this list has never heard of.
export const stepLabel = (v) => STEP_LABEL[v] || String(v || "");
// gate -> { color (antd Tag preset), text } or null when there is none.
export function gateTag(gate) {
  if (!gate || !gate.state) return null;
  const d = gate.signOffDate ? String(gate.signOffDate).slice(0, 10) : "";
  switch (gate.state) {
    case "complete": return { color: "green", text: gate.startedEarly ? `Complete (started ${gate.startedEarly}d early)` : "Complete" };
    case "released": return { color: "green", text: "Released" };
    case "waiting": return { color: "blue", text: `Waiting sign-off${d ? ` ${d}` : ""}` };
    case "early_approved": return { color: "gold", text: "Early (approved)" };
    case "early_requested": return { color: "orange", text: "Early requested" };
    case "out_of_sequence": return { color: "red", text: `Out of sequence${gate.daysEarly ? ` ${gate.daysEarly}d` : ""}` };
    // "No sign-off" read as "nothing to do here". It means the opposite:
    // this package has no BIM sign-off row, so nothing gates its install.
    case "no_signoff": return { color: "default", text: "No sign-off row - ungated" };
    default: return { color: "default", text: String(gate.state).replace(/_/g, " ") };
  }
}

export const WEEKDAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

export const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

export const dayDiff = (a, b) =>
  Math.round((startOfDay(a) - startOfDay(b)) / DAY_MS);

// Shift a date by whole days (DST-safe via setDate).
export const shiftDays = (d, days) => {
  const x = startOfDay(d);
  x.setDate(x.getDate() + days);
  return x;
};

// Local yyyy-mm-dd (no UTC drift for evening users east of Greenwich).
export const toIsoDate = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const fmt = (d) =>
  d.toLocaleDateString(undefined, { month: "short", day: "numeric" });

// Flatten items into rows with depth: roots first (sort order kept),
// each followed by its children, indented.
export function flattenTree(items, collapsed) {
  const byParent = new Map();
  const ids = new Set(items.map((i) => String(i._id)));
  const roots = [];
  for (const it of items) {
    const pid = it.parentId ? String(it.parentId) : null;
    if (pid && ids.has(pid)) {
      if (!byParent.has(pid)) byParent.set(pid, []);
      byParent.get(pid).push(it);
    } else {
      roots.push(it);
    }
  }
  const out = [];
  // `collapsed` (optional Set of item ids) hides the subtree under those
  // ids; rows carry hasChildren / collapsed so the table can draw the
  // expand / collapse triangle.
  const visit = (it, depth) => {
    const id = String(it._id);
    const kids = byParent.get(id) || [];
    const isCollapsed = !!(collapsed && collapsed.has(id));
    out.push({ item: it, depth, hasChildren: kids.length > 0, collapsed: isCollapsed });
    if (isCollapsed) return;
    for (const c of kids) visit(c, depth + 1);
  };
  for (const r of roots) visit(r, 0);
  return out;
}

// One lane per responsibleCompanyId (feed order kept inside a lane,
// lane order = first appearance). Items without a company go last
// under "Unassigned". Group rows are {group, key}; item rows keep the
// same {item, depth} shape flattenTree produces.
// One lane per distinct value of `keyOf`, in first-seen order, with the
// blank ("Unassigned") lane last. Contractor lanes were the first use of
// this; area, discipline and stage are the same shape, and the schedule
// is only worth grouping by them now that all three are picked from a
// list instead of typed.
export function buildGroupedRows(items, keyOf, labelOf, collapsed, prefix = "grp", emptyLabel = "Unassigned") {
  const lanes = new Map();
  for (const it of items || []) {
    const k = String(keyOf(it) || "");
    if (!lanes.has(k)) lanes.set(k, []);
    lanes.get(k).push(it);
  }
  const keys = [...lanes.keys()].filter((k) => k !== "");
  if (lanes.has("")) keys.push("");
  const out = [];
  for (const k of keys) {
    const key = `${prefix}-${k || "unassigned"}`;
    const isCollapsed = !!(collapsed && collapsed.has(key));
    out.push({
      group: k ? labelOf(k, lanes.get(k)) : emptyLabel,
      key, hasChildren: true, collapsed: isCollapsed,
    });
    if (isCollapsed) continue;
    for (const it of lanes.get(k)) out.push({ item: it, depth: 1 });
  }
  return out;
}

export function buildCompanyRows(items, companyNames, collapsed) {
  const lanes = new Map();
  for (const it of items || []) {
    const cid = it.responsibleCompanyId ? String(it.responsibleCompanyId) : "";
    if (!lanes.has(cid)) lanes.set(cid, []);
    lanes.get(cid).push(it);
  }
  const cids = [...lanes.keys()].filter((c) => c !== "");
  if (lanes.has("")) cids.push("");
  const out = [];
  for (const cid of cids) {
    const label = cid ? (companyNames && companyNames[cid]) || cid : "Unassigned";
    const key = `lane-${cid || "unassigned"}`;
    const isCollapsed = !!(collapsed && collapsed.has(key));
    out.push({ group: label, key, hasChildren: true, collapsed: isCollapsed });
    if (isCollapsed) continue;
    for (const it of lanes.get(cid)) out.push({ item: it, depth: 1 });
  }
  return out;
}

// Expand a meeting's recurrence into occurrence dates inside the window.
export function expandOccurrences(item, windowStart, windowEnd) {
  const start = startOfDay(new Date(item.startDate));
  const rec = item.recurrence;
  if (!rec || !rec.freq) {
    return start >= windowStart && start <= windowEnd ? [start] : [];
  }
  const until = startOfDay(new Date(rec.until || item.endDate || item.startDate));
  const from = start > windowStart ? start : windowStart;
  const to = until < windowEnd ? until : windowEnd;
  if (from > to) return [];

  const byDay = (rec.byDay || []).filter((d) => WEEKDAYS.indexOf(d) >= 0);
  const anchorDow = start.getDay();
  const out = [];
  for (let t = from.getTime(); t <= to.getTime(); t += DAY_MS) {
    const d = new Date(t);
    const dow = WEEKDAYS[d.getDay()];
    let hit = false;
    if (rec.freq === "daily") {
      hit = true;
    } else if (rec.freq === "weekly") {
      hit = byDay.length ? byDay.indexOf(dow) >= 0 : d.getDay() === anchorDow;
    } else if (rec.freq === "biweekly") {
      const dowOk = byDay.length ? byDay.indexOf(dow) >= 0 : d.getDay() === anchorDow;
      const weeks = Math.floor(dayDiff(d, start) / 7);
      hit = dowOk && weeks % 2 === 0;
    } else if (rec.freq === "monthly") {
      hit = d.getDate() === start.getDate();
    }
    if (hit) out.push(d);
  }
  return out;
}

// Finish-to-start elbow between a predecessor's bar end and a
// successor's bar start. Returns an SVG path `d` string.
export function depArrowPath(x1, y1, x2, y2, rowH) {
  const midX = x1 + 8;
  if (x2 - 8 >= midX) {
    return `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
  }
  // Successor starts left of (or under) the predecessor's end: route
  // around through the gap between the two rows.
  const yMid = y2 > y1 ? y2 - rowH / 2 : y2 + rowH / 2;
  return (
    `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${yMid} ` +
    `L ${x2 - 8} ${yMid} L ${x2 - 8} ${y2} L ${x2} ${y2}`
  );
}
